import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Formik } from "formik";
import * as Yup from "yup";

import { createInvoiceGroup, updateInvoiceGroup } from "src/api/invoice";
import { TextAreaField, TextField } from "src/components/form";
import { Button } from "src/components/ui/button";
import { DialogClose } from "src/components/ui/dialog";
import { usePortalContext } from "src/context/PortalContext";
import { useAuth } from "src/providers/AuthProvider";
import { getCustomizationValue, showSuccessToast } from "src/lib/utils";

const validationSchema = Yup.object().shape({
	name: Yup.string().required("This field is required"),
	description: Yup.string(),
});

interface InvoiceGroupFormValues {
	uuid?: string;
	name: string;
	description: string;
}

export interface InvoiceGroup {
	uuid?: string;
	name?: string;
	description?: string;
}

interface CreateInvoiceGroupProps {
	/** Only supplied when editing an existing group. */
	invoiceGroup?: InvoiceGroup;
}

const CreateInvoiceGroup = ({ invoiceGroup }: CreateInvoiceGroupProps) => {
	const { user } = useAuth();
	const queryClient = useQueryClient();
	const closePortal = usePortalContext();

	const groupLabel = getCustomizationValue(user, "invoice_group_label", true) || "Group";

	const initialValues: InvoiceGroupFormValues = {
		uuid: invoiceGroup?.uuid,
		name: invoiceGroup?.name ?? "",
		description: invoiceGroup?.description ?? "",
	};

	const { mutate, isLoading } = useMutation(invoiceGroup ? updateInvoiceGroup : createInvoiceGroup, {
		onSuccess: ({ message }) => {
			showSuccessToast(message ?? "");
			queryClient.invalidateQueries(["invoice-groups"]);
			closePortal();
		},
	});

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(values) => mutate(values)}>
			{({ values, handleChange, handleSubmit, errors, touched }) => (
				<form onSubmit={handleSubmit} className="tw-flex tw-flex-1 tw-flex-col tw-gap-4">
					<TextField
						label={`${groupLabel} name`}
						name="name"
						placeholder={`${groupLabel} name`}
						value={values.name}
						error={touched.name && errors.name}
						onChange={handleChange}
						required
					/>

					<TextAreaField
						label="Description"
						name="description"
						placeholder="What is this group for?"
						value={values.description}
						error={touched.description && errors.description}
						onChange={handleChange}
						containerClassName="tw-mb-auto"
					/>

					<div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
						<DialogClose asChild>
							<Button type="button" variant="ghost">
								Cancel
							</Button>
						</DialogClose>
						<Button type="submit" variant="secondary" disabled={isLoading}>
							{isLoading ? "Saving..." : "Save"}
						</Button>
					</div>
				</form>
			)}
		</Formik>
	);
};

export default CreateInvoiceGroup;
